/**
 * leadBus · a one-line pub/sub so every lead surface refreshes when any of them writes.
 *
 * **Why this exists** — the same reason `activityBus` does, and deliberately modelled on it.
 * There is no React Query in this codebase and therefore no shared cache to invalidate: `useLeads`
 * owns a private array and a private `refetch`, and so do the board, the drawer and the focus
 * counters. A write refreshed only the list you happened to be looking at.
 *
 * Task 1 made that visible in a way it had not been before. Assigning an urgency from a Kanban
 * card has to update the card, the table behind it, the lead drawer, the urgency filter's
 * membership AND the calendar — because the same lead colours its activities there. Five
 * surfaces, one write, and previously one of them would have known.
 *
 * **Calendar included, on purpose.** A lead's effective urgency IS its activities' calendar
 * priority, so a urgency change with a stale calendar leaves two screens disagreeing about the
 * same work. `useCalendar` subscribes to this for exactly that case.
 *
 * Adding a new lead-reading hook means adding
 * `useEffect(() => onLeadChange(refetch), [refetch])` and nothing else.
 */

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Subscribe to lead mutations. Returns the unsubscribe, so the intended use is
 * `useEffect(() => onLeadChange(refetch), [refetch])` — returning it directly makes the cleanup
 * automatic and a forgotten teardown impossible.
 */
export function onLeadChange(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

let flushHandle: ReturnType<typeof setTimeout> | null = null

/**
 * Announce that a lead changed in a way other surfaces must re-read.
 *
 * **Coalesced**, like `activityBus`: a bulk action or a fast double-selection would otherwise
 * refetch every subscribed list once per write and let the UI settle on whichever response landed
 * last. Bursts inside one tick collapse into a single refresh.
 *
 * Each listener is isolated — one throwing subscriber must not stop the rest from refreshing,
 * because the visible symptom of that is exactly the stale-list problem this file fixes.
 */
export function emitLeadChange(): void {
  if (flushHandle !== null) return
  flushHandle = setTimeout(() => {
    flushHandle = null
    for (const listener of [...listeners]) {
      try {
        listener()
      } catch {
        // A refetch that throws is the hook's own problem to surface; swallow it here so the
        // remaining surfaces still refresh.
      }
    }
  }, 0)
}
